import { injectable } from 'inversify'

/**
 * Wraps Intl.NumberFormat/Intl.DateTimeFormat (invariant 8: all browser APIs
 * behind a gateway). Locale is set once by whoever owns "current locale"
 * (I18nPresenter) and applies to every format call after that, so the rest
 * of the app doesn't have to thread a locale through every call site.
 */
@injectable()
export class FormatGateway {
  private locale = 'en'

  setLocale(locale: string): void {
    this.locale = locale
  }

  formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
    return new Intl.NumberFormat(this.locale, options).format(value)
  }

  formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(this.locale, options).format(date)
  }

  /** The IANA zone the browser is running in — e.g. 'Europe/Ljubljana'. */
  resolvedTimeZone(): string {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  /**
   * A chess-clock countdown — `mm:ss`, or `h:mm:ss` past an hour. Plain
   * arithmetic, not `Intl.DurationFormat` (patchy runtime support, and a
   * clock reads the same in every locale) — locale-sensitive formatting
   * belongs to `formatNumber`/`formatDate` above, this one deliberately
   * isn't. Negative/non-finite input clamps to `0:00`, the same "never
   * render garbage" posture the fake mirrors.
   */
  formatDuration(ms: number): string {
    const totalSeconds = Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 1000)) : 0
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (n: number) => n.toString().padStart(2, '0')
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`
  }
}
